import classNames from "../../../lib/utils/classNames";
import { INJECT_UICORE_BUS, INJECT_UICORE_DESIGNER } from "../../../lib/utils/consts";
import { ArrayItemNode } from './ArrayItemsTree';
const getStubName = stub => stub.schema.title || stub.schema.id || '(未命名)';
const actionPositionsInChildren = new Set(['before-children', 'after-children']);

/**
 * 用来把 DataStub 转换为可以渲染的树的组件
 *
 * @type {Vue.ComponentOptions<Vue>}
 */
export const DSTreeBase = {
  name: 'DSTreeBase',
  props: {
    stub: Object,
    preferredName: {
      type: String,
      default: ''
    },
    // 默认可以不传
    depth: {
      type: Number,
      default: 0
    },
    sticky: {
      default: true
    }
  },
  inject: {
    uc: {
      from: INJECT_UICORE_BUS
    },
    designer: {
      from: INJECT_UICORE_DESIGNER
    },
    dataSourceTreeContext: {}
  },
  data() {
    return {
      expanded: false
    };
  },
  computed: {
    expandable() {
      return this.children.length > 0 || this.actions.some(x => actionPositionsInChildren.has(x.position)) || this.arrayExpandable || this.enumExpandable;
    },
    arrayExpandable() {
      var _schema$items;
      const {
        schema
      } = this.stub;
      return this.dataSourceTreeContext.showArrayItemsTree && schema.type === 'array' && ((_schema$items = schema.items) === null || _schema$items === void 0 || (_schema$items = _schema$items.fields) === null || _schema$items === void 0 ? void 0 : _schema$items.length) > 0;
    },
    enumExpandable() {
      const enums = this.stub.getEnums();
      return (enums === null || enums === void 0 ? void 0 : enums.length) > 0;
    },
    children() {
      const {
        stub
      } = this;
      if (stub.type === 'object') {
        const {
          fields,
          candidates
        } = stub.getFields();
        const allField = [...fields, ...candidates].map(({
          id
        }) => {
          const subStub = stub.getChildStub(id);
          return {
            id,
            stub: subStub,
            name: getStubName(subStub)
          };
        });
        return allField;
      }
      return [];
    },
    id() {
      return this.stub.schema.id || '';
    },
    dataPath() {
      return this.stub.path;
    },
    /**
     * @this {{
     *  uc: UICore.UcBus,
     *  designer: UICore.Designer.Designer,
     *  stub: import('@tencent/data-schema-core').DataStub
     * }}
     */
    actions() {
      if (!this.dataSourceTreeContext.showActions) return [];
      const dataStub = this.stub;

      /** @type {UICore.DataSourceAction[]} */
      const list = [];
      return this.uc.hooks.getActionsOfDataSource.call(list, {
        uc: this.uc,
        designer: this.designer,
        renderer: this.designer.renderer,
        dataStub,
        dataPath: dataStub.path,
        dataSchema: dataStub.schema
      });
    },
    name() {
      return this.preferredName || getStubName(this.stub);
    },
    isRoot() {
      return this.depth === 0;
    }
  },
  methods: {
    toggleExpansion() {
      this.expanded = !this.expanded;
    },
    renderChildrenNodes(h) {
      var _$scopedSlots$before, _$scopedSlots$afterC;
      const {
        enumExpandable,
        expanded,
        depth,
        isRoot,
        dataSourceTreeContext,
        arrayExpandable,
        children,
        stub
      } = this;
      const {
        indentWidth,
        $scopedSlots
      } = dataSourceTreeContext;
      if (!expanded) return;
      // enum
      if (enumExpandable) {
        return h(ArrayItemNode, {
          "style": "margin-left:17px",
          "attrs": {
            "showChildrenOnly": true,
            "schema": stub.schema
          }
        });
      }
      // array
      if (arrayExpandable) {
        return h(ArrayItemNode, {
          "style": "margin-left:17px",
          "attrs": {
            "showChildrenOnly": true,
            "schema": stub.schema
          }
        });
      }
      // object
      return h("div", {
        "class": "ucd-dataTree-childrenWrapper"
      }, [!isRoot && h("div", {
        "class": "ucd-dataTree-childrenIndent",
        "style": {
          width: indentWidth,
          flexBasis: indentWidth
        }
      }), h("div", {
        "class": "ucd-dataTree-children"
      }, [(_$scopedSlots$before = $scopedSlots['before-children']) === null || _$scopedSlots$before === void 0 ? void 0 : _$scopedSlots$before.call($scopedSlots, this), children.map(child => h('DSTreeBase', {
        key: child.id,
        props: {
          stub: child.stub,
          name: child.name,
          depth: depth + 1
        }
      })), (_$scopedSlots$afterC = $scopedSlots['after-children']) === null || _$scopedSlots$afterC === void 0 ? void 0 : _$scopedSlots$afterC.call($scopedSlots, this)])]);
    }
  },
  created() {
    if (this.arrayExpandable) {
      // 类型树默认不展开
      return;
    }
    this.$watch(() => {
      var _this$stub;
      return (_this$stub = this.stub) === null || _this$stub === void 0 || (_this$stub = _this$stub.schema) === null || _this$stub === void 0 ? void 0 : _this$stub['uc:collapsed'];
    }, newVal => {
      this.expanded = !(newVal !== null && newVal !== void 0 ? newVal : this.depth > 1);
    }, {
      immediate: true
    });
  },
  mounted() {},
  /**
   * @param {Vue.CreateElement} h
   */
  render(h) {
    var _$scopedSlots$node;
    const {
      expandable,
      stub,
      depth,
      dataPath,
      dataSourceTreeContext,
      isRoot
    } = this;
    const {
      fieldFilter,
      $scopedSlots,
      showHiddenFields
    } = dataSourceTreeContext;
    if (fieldFilter && !fieldFilter[dataPath]) return null;
    if (!showHiddenFields && this.dataSourceTreeContext.getFieldHidden(stub.schema)) return null;
    return h("div", {
      "class": "ucd-dataTree",
      "attrs": {
        "data-expandable": expandable,
        "data-depth": depth
      }
    }, [h("div", {
      "class": classNames('ucd-dataTree-nodeContainer', isRoot && this.sticky && 'ucd-dataTree-nodeContainer-sticky'),
      "attrs": {
        "title": stub.path
      }
    }, [(_$scopedSlots$node = $scopedSlots.node) === null || _$scopedSlots$node === void 0 ? void 0 : _$scopedSlots$node.call($scopedSlots, this)]), this.renderChildrenNodes(h)]);
  }
};
//# sourceMappingURL=DSTreeBase.js.map
